import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar as LicenseIcon,
  Upload as UploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  PhotoCamera as PhotoIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';

interface License {
  id: string;
  province: string;
  class: string;
  licenseNumber: string;
  issueDate: string;
  expiryDate: string;
  endorsements?: string[];
  documentFileId?: number;
  documentFile?: {
    id: number;
    url: string;
    name: string;
    size: number;
  };
}

interface DrivingLicensesProps {
  data: License[];
  onChange: (data: License[]) => void;
}

const provinces = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'YT', label: 'Yukon' },
];

const licenseClasses = [
  { value: 'Class 1', label: 'Class 1 - Tractor-trailers, buses' },
  { value: 'Class 2', label: 'Class 2 - Buses, large trucks' },
  { value: 'Class 3', label: 'Class 3 - Large trucks' },
  { value: 'Class 4', label: 'Class 4 - Buses, ambulances' },
  { value: 'Class 5', label: 'Class 5 - Regular vehicles' },
  { value: 'Class G', label: 'Class G - Regular vehicles (Ontario)' },
  { value: 'Class A', label: 'Class A - Commercial' },
  { value: 'Class B', label: 'Class B - Commercial' },
  { value: 'Class C', label: 'Class C - Commercial' },
  { value: 'Class D', label: 'Class D - Commercial' },
  { value: 'Class E', label: 'Class E - Commercial' },
];

const DrivingLicenses: React.FC<DrivingLicensesProps> = ({ data, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);
  const [formData, setFormData] = useState<Partial<License>>({});
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleAdd = () => {
    setEditingLicense(null);
    setFormData({});
    setDialogOpen(true);
  };

  const handleEdit = (license: License) => {
    setEditingLicense(license);
    setFormData(license);
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    onChange(data.filter(license => license.id !== id));
  };

  const handleSave = () => {
    if (!formData.province || !formData.class || !formData.licenseNumber) {
      return;
    }

    const licenseData: License = {
      id: editingLicense?.id || Date.now().toString(),
      province: formData.province!,
      class: formData.class!,
      licenseNumber: formData.licenseNumber!,
      issueDate: formData.issueDate || '',
      expiryDate: formData.expiryDate || '',
      endorsements: formData.endorsements || [],
    };

    if (editingLicense) {
      onChange(data.map(license => license.id === editingLicense.id ? licenseData : license));
    } else {
      onChange([...data, licenseData]);
    }

    setDialogOpen(false);
    setFormData({});
    setEditingLicense(null);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setFormData({});
    setEditingLicense(null);
  };

  const updateFormData = (field: keyof License, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Document upload handlers
  const handleDocumentUpload = () => {
    setDocumentDialogOpen(true);
    setPreviewUrl(formData.documentFile?.url || '');
  };

  const handleDocumentDialogClose = () => {
    setDocumentDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl(formData.documentFile?.url || '');
    setIsDragOver(false);
  };

  const validateFile = (file: File): boolean => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return false;
    }
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) return;

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDocumentSave = () => {
    if (selectedFile) {
      // In a real implementation, you would upload the file to a server here
      // For now, we'll simulate saving the document info
      const mockFileData = {
        id: Date.now(),
        url: previewUrl,
        name: selectedFile.name,
        size: selectedFile.size,
      };

      updateFormData('documentFile', mockFileData);
      updateFormData('documentFileId', mockFileData.id);
    }
    handleDocumentDialogClose();
  };

  const handleRemoveDocument = () => {
    updateFormData('documentFile', undefined);
    updateFormData('documentFileId', undefined);
    handleDocumentDialogClose();
  };

  const isImage = (filename?: string) => {
    if (!filename) return false;
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Add your active driving licenses
      </Typography>

      <Stack spacing={3} mt={3}>
        {/* Add License Button */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            p: 2,
            borderStyle: 'dashed',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderStyle: 'dashed',
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            }
          }}
        >
          Add License
        </Button>

        {/* License Cards */}
        {data.map((license) => (
          <Card key={license.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={2} flex={1}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: '#e3f2fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LicenseIcon sx={{ color: '#1976d2' }} />
                  </Box>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {provinces.find(p => p.value === license.province)?.label} - {license.class}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      License #{license.licenseNumber}
                    </Typography>
                    {license.issueDate && (
                      <Typography variant="body2" color="text.secondary">
                        Issued: {new Date(license.issueDate).toLocaleDateString()}
                      </Typography>
                    )}
                    {license.expiryDate && (
                      <Typography variant="body2" color="text.secondary">
                        Expires: {new Date(license.expiryDate).toLocaleDateString()}
                      </Typography>
                    )}
                    {license.endorsements && license.endorsements.length > 0 && (
                      <Stack direction="row" spacing={1} mt={1}>
                        {license.endorsements.map((endorsement, index) => (
                          <Chip
                            key={index}
                            label={endorsement}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    )}
                    {license.documentFile && (
                      <Stack direction="row" spacing={1} mt={1} alignItems="center">
                        <InsertDriveFileIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                        <Typography variant="caption" color="success.main">
                          Document uploaded
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <IconButton onClick={() => handleEdit(license)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(license.id)} 
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}

        {data.length === 0 && (
          <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <LicenseIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No licenses added yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add your active driving licenses to complete this section
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Add/Edit License Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingLicense ? 'Edit License' : 'Add an active Drivers License'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>* Select province</FormLabel>
                <Select
                  value={formData.province || ''}
                  onChange={(e) => updateFormData('province', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select province
                  </MenuItem>
                  {provinces.map((province) => (
                    <MenuItem key={province.value} value={province.value}>
                      {province.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>* Select class</FormLabel>
                <Select
                  value={formData.class || ''}
                  onChange={(e) => updateFormData('class', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select class
                  </MenuItem>
                  {licenseClasses.map((licenseClass) => (
                    <MenuItem key={licenseClass.value} value={licenseClass.value}>
                      {licenseClass.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>* License Number</FormLabel>
                <TextField
                  value={formData.licenseNumber || ''}
                  onChange={(e) => updateFormData('licenseNumber', e.target.value)}
                  placeholder="Enter your license number"
                  fullWidth
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Issue Date</FormLabel>
                <TextField
                  type="date"
                  value={formData.issueDate || ''}
                  onChange={(e) => updateFormData('issueDate', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Expiry Date</FormLabel>
                <TextField
                  type="date"
                  value={formData.expiryDate || ''}
                  onChange={(e) => updateFormData('expiryDate', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>

            {/* Document Upload Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 2 }}>License Document (Optional)</FormLabel>
                <Box
                  sx={{
                    width: '100%',
                    height: 120,
                    borderRadius: 2,
                    border: '2px dashed #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#f9f9f9',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: '#f3f7ff',
                    },
                  }}
                  onClick={handleDocumentUpload}
                >
                  {formData.documentFile ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <InsertDriveFileIcon sx={{ fontSize: 32, color: '#1976d2' }} />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {formData.documentFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(formData.documentFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="primary">
                        Click to replace
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack alignItems="center" spacing={1}>
                      <UploadIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Click to upload license scan or photo
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PDF, JPG, PNG (max 10MB)
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={!formData.province || !formData.class || !formData.licenseNumber}
            sx={{
              backgroundColor: '#212121',
              '&:hover': { backgroundColor: '#424242' }
            }}
          >
            {editingLicense ? 'Update' : 'Add License'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog 
        open={documentDialogOpen} 
        onClose={handleDocumentDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            License Document
            <IconButton onClick={handleDocumentDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {previewUrl ? (
              isImage(selectedFile?.name || formData.documentFile?.name) ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="License document preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: 2,
                    mb: 2,
                  }}
                />
              ) : (
                <Box sx={{ mb: 2, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <InsertDriveFileIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
                  <Typography variant="body1">
                    {selectedFile?.name || formData.documentFile?.name}
                  </Typography>
                </Box>
              )
            ) : (
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('license-file-input')?.click()}
                sx={{
                  width: '100%',
                  height: 200,
                  border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  backgroundColor: isDragOver ? '#f3f7ff' : '#f9f9f9',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: '#f3f7ff',
                  },
                }}
              >
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <UploadIcon sx={{ fontSize: 48, mb: 1, color: isDragOver ? '#1976d2' : 'inherit' }} />
                  <Typography variant="body2" sx={{ color: isDragOver ? '#1976d2' : 'inherit' }}>
                    {isDragOver ? 'Drop file here' : 'Drag and drop file here or click to select'}
                  </Typography>
                </Box>
              </Box>
            )}
            
            <input
              accept="image/*,.pdf,.doc,.docx"
              style={{ display: 'none' }}
              id="license-file-input"
              type="file"
              onChange={handleInputChange}
            />
            <label htmlFor="license-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<InsertDriveFileIcon />}
                sx={{ mr: 1 }}
              >
                Select File
              </Button>
            </label>
            
            {formData.documentFile && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemoveDocument}
                sx={{ ml: 1 }}
              >
                Remove Document
              </Button>
            )}
            
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
              Maximum file size: 10MB • Supported formats: Images, PDF, DOC, DOCX
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDocumentDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDocumentSave}
            disabled={!selectedFile && !formData.documentFile}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DrivingLicenses;